import React, { forwardRef, useImperativeHandle, useRef, useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { Stage, Layer, Line, Arrow, Rect, Text, Image as KonvaImage } from 'react-konva';

const COLOR = '#e53935';
const STROKE_WIDTH = 3;

let shapeIdCounter = 0;
const nextShapeId = () => `shape_${++shapeIdCounter}`;

/**
 * Draws freehand pen strokes, arrows, rectangles, and text labels on top of a
 * captured screenshot. Scales the stage down to fit the available width/height
 * (shapes are stored in natural/un-scaled image coordinates via
 * getRelativePointerPosition, so annotations stay correctly placed regardless
 * of display size), and exports the flattened result at full natural
 * resolution. Exposes undo/clear/getDataUrl to the parent dialog via ref.
 */
const AnnotationCanvas = forwardRef(({ imageSrc, tool }, ref) => {
    const [image, setImage] = useState(null);
    const [shapes, setShapes] = useState([]);
    const [undoStack, setUndoStack] = useState([]);
    const [drawing, setDrawing] = useState(false);
    const [textInput, setTextInput] = useState(null);
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
    const stageRef = useRef(null);
    const containerRef = useRef(null);
    const textInputRef = useRef(null);
    const activeShapeId = useRef(null);

    useEffect(() => {
        if (!imageSrc) return;
        const img = new window.Image();
        img.src = imageSrc;
        img.onload = () => setImage(img);
    }, [imageSrc]);

    // Track the available body area (both dimensions) so the stage can scale down to
    // completely fit it — ResizeObserver catches the dialog's own open transition
    // settling, not just window resizes. useLayoutEffect (not useEffect) so this measures
    // and applies before the browser paints — otherwise the Stage briefly renders at
    // scale 1 (full natural-pixel size, often thousands of px tall) on the frame the image
    // loads, then snaps down once measured; that flash/snap left the dialog body scrolled
    // into now-empty space, i.e. blank white area at the top when scrolling.
    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return undefined;
        const measure = () => setContainerSize({ width: el.clientWidth, height: el.clientHeight });
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [image]);

    const naturalWidth = image?.width || 1;
    const naturalHeight = image?.height || 1;
    // Always fill the full width of the modal body; height follows the image's aspect
    // ratio and the body scrolls vertically if it doesn't fit entirely on screen.
    // Fall back to 0 (not 1) until the container is measured, so the Stage never briefly
    // renders at full natural-pixel size — see useLayoutEffect note above.
    const scale = image && containerSize.width ? containerSize.width / naturalWidth : 0;
    const displayWidth = naturalWidth * scale;
    const displayHeight = naturalHeight * scale;

    // Konva's Stage grabs focus on its own container on pointerdown (for keyboard
    // support), which races with — and wins over — a plain `autoFocus` on this
    // overlay input. Focusing on the next frame instead lets it win that race.
    useEffect(() => {
        if (!textInput) return;
        const raf = requestAnimationFrame(() => textInputRef.current?.focus());
        return () => cancelAnimationFrame(raf);
    }, [textInput]);

    const pushUndoSnapshot = () => setUndoStack((prev) => [...prev, shapes]);

    // Natural (un-scaled) coordinates, so shapes line up with the image regardless of display scale.
    const getNaturalPointerPosition = useCallback(() => {
        const stage = stageRef.current;
        return stage ? stage.getRelativePointerPosition() : { x: 0, y: 0 };
    }, []);

    const handlePointerDown = () => {
        const pos = getNaturalPointerPosition();

        if (tool === 'text') {
            setTextInput({ x: pos.x, y: pos.y, value: '' });
            return;
        }

        const id = nextShapeId();
        activeShapeId.current = id;
        pushUndoSnapshot();

        let shape = null;
        if (tool === 'pen') shape = { id, type: 'line', points: [pos.x, pos.y] };
        else if (tool === 'arrow') shape = { id, type: 'arrow', points: [pos.x, pos.y, pos.x, pos.y] };
        else if (tool === 'rect') shape = { id, type: 'rect', x: pos.x, y: pos.y, width: 0, height: 0 };

        if (shape) {
            setShapes((prev) => [...prev, shape]);
            setDrawing(true);
        }
    };

    const handlePointerMove = () => {
        if (!drawing) return;
        const pos = getNaturalPointerPosition();
        setShapes((prev) =>
            prev.map((s) => {
                if (s.id !== activeShapeId.current) return s;
                if (s.type === 'line') return { ...s, points: [...s.points, pos.x, pos.y] };
                if (s.type === 'arrow') return { ...s, points: [s.points[0], s.points[1], pos.x, pos.y] };
                if (s.type === 'rect') return { ...s, width: pos.x - s.x, height: pos.y - s.y };
                return s;
            })
        );
    };

    const handlePointerUp = () => {
        setDrawing(false);
        activeShapeId.current = null;
    };

    const commitText = () => {
        if (textInput && textInput.value.trim()) {
            pushUndoSnapshot();
            setShapes((prev) => [...prev, { id: nextShapeId(), type: 'text', x: textInput.x, y: textInput.y, text: textInput.value.trim() }]);
        }
        setTextInput(null);
    };

    useImperativeHandle(ref, () => ({
        undo: () => {
            setUndoStack((prev) => {
                if (!prev.length) return prev;
                setShapes(prev[prev.length - 1]);
                return prev.slice(0, -1);
            });
        },
        clear: () => {
            pushUndoSnapshot();
            setShapes([]);
        },
        hasShapes: shapes.length > 0,
        canUndo: undoStack.length > 0,
        // Export at full natural resolution regardless of how small it's displayed.
        getDataUrl: () => (stageRef.current ? stageRef.current.toDataURL({ mimeType: 'image/png', pixelRatio: scale > 0 ? 1 / scale : 1 }) : null)
    }));

    return (
        <div ref={containerRef} style={{ width: '100%', minHeight: '100%' }}>
            {!image ? (
                <div className="flex align-items-center justify-content-center" style={{ minHeight: 200, color: '#94a3b8' }}>
                    Loading screenshot…
                </div>
            ) : (
                <div style={{ position: 'relative', width: displayWidth, height: displayHeight, border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                    <Stage
                        ref={stageRef}
                        width={displayWidth}
                        height={displayHeight}
                        scaleX={scale}
                        scaleY={scale}
                        onMouseDown={handlePointerDown}
                        onMouseMove={handlePointerMove}
                        onMouseUp={handlePointerUp}
                        onTouchStart={handlePointerDown}
                        onTouchMove={handlePointerMove}
                        onTouchEnd={handlePointerUp}
                        style={{ cursor: tool === 'text' ? 'text' : 'crosshair', display: 'block' }}
                    >
                        <Layer>
                            <KonvaImage image={image} />
                            {shapes.map((s) => {
                                if (s.type === 'line') return <Line key={s.id} points={s.points} stroke={COLOR} strokeWidth={STROKE_WIDTH / scale} tension={0} lineCap="round" lineJoin="round" />;
                                if (s.type === 'arrow')
                                    return (
                                        <Arrow
                                            key={s.id}
                                            points={s.points}
                                            stroke={COLOR}
                                            fill={COLOR}
                                            strokeWidth={STROKE_WIDTH / scale}
                                            pointerLength={12 / scale}
                                            pointerWidth={12 / scale}
                                        />
                                    );
                                if (s.type === 'rect') return <Rect key={s.id} x={s.x} y={s.y} width={s.width} height={s.height} stroke={COLOR} strokeWidth={STROKE_WIDTH / scale} />;
                                if (s.type === 'text') return <Text key={s.id} x={s.x} y={s.y} text={s.text} fill={COLOR} fontSize={20 / scale} fontStyle="bold" />;
                                return null;
                            })}
                        </Layer>
                    </Stage>
                    {textInput && (
                        <input
                            ref={textInputRef}
                            value={textInput.value}
                            onChange={(e) => setTextInput((t) => ({ ...t, value: e.target.value }))}
                            onBlur={commitText}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') commitText();
                                if (e.key === 'Escape') setTextInput(null);
                            }}
                            style={{
                                position: 'absolute',
                                left: textInput.x * scale,
                                top: textInput.y * scale,
                                border: `1px solid ${COLOR}`,
                                color: COLOR,
                                fontWeight: 'bold',
                                fontSize: 14,
                                padding: '2px 4px',
                                background: 'rgba(255,255,255,0.95)',
                                zIndex: 10
                            }}
                        />
                    )}
                </div>
            )}
        </div>
    );
});

export default AnnotationCanvas;
